import React, { useState } from 'react';
import { Inbox, Copy, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import Tooltip from '../common/Tooltip';

/**
 * Queue Read output structure from backend
 */
export interface QueueReadOutput {
  success: boolean;
  status: 'items_read' | 'no_items' | 'circuit_open' | 'queue_not_found' | 'permission_denied';
  items_read: number;
  queue_id: number;
  queue_name: string;
  consumption_mode: 'single' | 'batch';
  batch_size?: number;
  circuit_open?: boolean;
  reason?: string;
  item_ids?: string[];
  items?: Array<{
    item_id: string;
    payload: any;
    status: string;
    created_at: string;
  }>;
  error?: string;
}

/**
 * Type guard to check if output is a valid QueueReadOutput
 */
export function isQueueReadOutput(output: any): output is QueueReadOutput {
  return (
    output &&
    typeof output === 'object' &&
    'status' in output &&
    ['items_read', 'no_items', 'circuit_open', 'queue_not_found', 'permission_denied'].includes(output.status) &&
    'queue_id' in output
  );
}

interface QueueReadOutputRendererProps {
  output: QueueReadOutput;
  onCopy?: (text: string) => void;
}

/**
 * Custom renderer for queue_read step output.
 * Displays a formatted view of queue read results including:
 * - Summary cards (items read, queue name, consumption mode)
 * - Circuit breaker warning if applicable
 * - Expandable items list with payload previews
 */
const QueueReadOutputRenderer: React.FC<QueueReadOutputRendererProps> = ({ output, onCopy }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const truncatePayload = (payload: any, maxLength: number = 100): string => {
    const str = typeof payload === 'string' ? payload : JSON.stringify(payload);
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  };

  const getStatusConfig = () => {
    switch (output.status) {
      case 'items_read':
        return {
          icon: CheckCircle,
          color: 'text-green-600 dark:text-green-400',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          label: 'Items Read Successfully'
        };
      case 'no_items':
        return {
          icon: Inbox,
          color: 'text-gray-500 dark:text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-gray-900',
          borderColor: 'border-gray-200 dark:border-gray-700',
          label: 'No Items Available'
        };
      case 'circuit_open':
        return {
          icon: AlertTriangle,
          color: 'text-amber-600 dark:text-amber-400',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20',
          borderColor: 'border-amber-200 dark:border-amber-800',
          label: 'Circuit Breaker Open'
        };
      default:
        return {
          icon: AlertTriangle,
          color: 'text-red-600 dark:text-red-400',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          label: output.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        };
    }
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className={`${statusConfig.bgColor} border ${statusConfig.borderColor} rounded-lg p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${statusConfig.bgColor} rounded-full flex items-center justify-center border ${statusConfig.borderColor}`}>
              <Inbox className={`w-5 h-5 ${statusConfig.color}`} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Queue Read Results</h3>
              <div className="flex items-center space-x-2 mt-0.5">
                <StatusIcon className={`w-4 h-4 ${statusConfig.color}`} />
                <span className={`text-sm font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Items Read</p>
              <p className="text-xl font-bold text-gray-900 dark:text-warm-white">{output.items_read}</p>
            </div>
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
              <Inbox className="w-4 h-4 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Queue</p>
            <Tooltip content={output.queue_name}>
            <p className="text-sm font-semibold text-gray-900 dark:text-warm-white truncate">
              {output.queue_name}
            </p>
            </Tooltip>
            <p className="text-xs text-gray-400">ID: {output.queue_id}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-cap-dark rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Mode</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-warm-white capitalize">
              {output.consumption_mode}
              {output.consumption_mode === 'batch' && output.batch_size && (
                <span className="text-gray-500 dark:text-gray-400 font-normal"> ({output.batch_size})</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Circuit Breaker Warning */}
      {output.status === 'circuit_open' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Circuit Breaker Open</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                {output.reason || 'The circuit breaker for this queue is currently open. No items were read.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {output.error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300">Error</h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{output.error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      {output.items && output.items.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Items ({output.items.length})
            </h4>
            {output.item_ids && output.item_ids.length > 0 && (
              <button
                onClick={() => handleCopy(output.item_ids!.join('\n'))}
                className="inline-flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
              >
                <Copy className="w-3 h-3" />
                <span>Copy All IDs</span>
              </button>
            )}
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {output.items.map((item, index) => (
              <div key={item.item_id || index} className="bg-white dark:bg-cap-dark">
                <button
                  onClick={() => toggleItemExpanded(item.item_id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {expandedItems.has(item.item_id) ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <div className="text-left">
                      <span className="text-sm font-mono text-purple-600">#{item.item_id}</span>
                      {!expandedItems.has(item.item_id) && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                          {truncatePayload(item.payload)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      <span>{formatTimestamp(item.created_at)}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy(item.item_id);
                      }}
                      className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                    >
                      <Tooltip content="Copy ID">
                      <Copy className="w-3 h-3" />
                      </Tooltip>
                    </button>
                  </div>
                </button>
                {expandedItems.has(item.item_id) && (
                  <div className="px-4 pb-3 pl-11">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Payload</span>
                        <button
                          onClick={() => handleCopy(JSON.stringify(item.payload, null, 2))}
                          className="inline-flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </button>
                      </div>
                      <pre className="text-xs font-mono text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-x-auto">
                        {JSON.stringify(item.payload, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Items Message */}
      {output.status === 'no_items' && (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
          <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No items were available in the queue</p>
        </div>
      )}
    </div>
  );
};

export default QueueReadOutputRenderer;
